import { useEffect, useMemo, useState } from 'react';
import {
  useAudioVideo,
  useRemoteVideoTileState,
  useRosterState,
  useToggleLocalMute,
} from 'amazon-chime-sdk-component-library-react';
import { normalizeAttendeeId } from '../utils/participantUtils';

export const getCandidateAttendeeIds = (user = {}) => [
  user.participantId,
  user._id,
  user.user?._id,
  user.attendeeId,
  user.userId,
  user.externalUserId,
  user.id,
].map((value) => normalizeAttendeeId(value)).filter(Boolean);

const getChimeExternalUserId = (user = {}) => {
  const candidateId = user._id || user.user?._id || user.participantId || user.id || '';
  if (!candidateId) {
    return '';
  }
  return `novotalk_${String(candidateId).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
};

const normalizeRosterEntry = (entry, attendeeId) => ({
  attendeeId: normalizeAttendeeId(attendeeId),
  externalUserId: normalizeAttendeeId(entry?.externalUserId || ''),
  name: entry?.name || '',
  chimeAttendeeId: normalizeAttendeeId(entry?.chimeAttendeeId || ''),
});

const userMatchesRosterEntry = (user = {}, rosterEntry) => {
  if (!rosterEntry || !user) {
    return false;
  }

  const userCandidateIds = getCandidateAttendeeIds(user);
  if (userCandidateIds.some((id) => id === rosterEntry.attendeeId || id === rosterEntry.chimeAttendeeId)) {
    return true;
  }

  const expectedExternalPrefix = getChimeExternalUserId(user);
  if (!expectedExternalPrefix) {
    return false;
  }

  return rosterEntry.externalUserId.startsWith(expectedExternalPrefix);
};

const useParticipantStatuses = (users = [], options = {}) => {
  const audioVideo = useAudioVideo();
  const { roster } = useRosterState();
  const { tiles: remoteVideoTilesRaw } = useRemoteVideoTileState();
  const { muted: localMuted } = useToggleLocalMute();
  const { localCameraOn = null } = options;
  const remoteVideoTiles = remoteVideoTilesRaw || [];
  const [volumeIndicators, setVolumeIndicators] = useState({});
  const [seenExternalIds, setSeenExternalIds] = useState(() => new Set());
  const rosterEntries = useMemo(() => Object.entries(roster || {}).map(([attendeeId, entry]) => normalizeRosterEntry(entry, attendeeId)), [roster]);

  useEffect(() => {
    if (!audioVideo) {
      return () => {};
    }

    const subscriptions = [];

    (users || []).forEach((user) => {
      if (user?.self) {
        return;
      }

      const matchingRosterEntry = rosterEntries.find((entry) => userMatchesRosterEntry(user, entry));
      const attendeeId = matchingRosterEntry?.attendeeId || '';
      if (!attendeeId || subscriptions.some((entry) => entry.attendeeId === attendeeId)) {
        return;
      }

      const callback = (_id, _volume, muted) => {
        const isMuted = muted === true;
        setVolumeIndicators((current) => {
          if (current[attendeeId]?.muted === isMuted) {
            return current;
          }
          return {
            ...current,
            [attendeeId]: { muted: isMuted },
          };
        });
      };

      try {
        audioVideo.realtimeSubscribeToVolumeIndicator(attendeeId, callback);
        subscriptions.push({ attendeeId, callback });
      } catch (_error) {
        // Non-fatal. Some attendees may not have volume updates yet.
      }
    });

    return () => {
      subscriptions.forEach(({ attendeeId, callback }) => {
        try {
          audioVideo.realtimeUnsubscribeToVolumeIndicator(attendeeId, callback);
        } catch (_error) {
          // Non-fatal.
        }
      });
    };
  }, [audioVideo, rosterEntries, users]);

  const activeVideoAttendeeIds = useMemo(() => new Set(
    remoteVideoTiles
      .map((tileId) => {
        try {
          const tile = audioVideo?.getVideoTile?.(tileId);
          const tileState = tile?.state?.() || tile?.tileState?.();
          return normalizeAttendeeId(tileState?.boundAttendeeId || tileState?.attendeeId);
        } catch (_error) {
          return '';
        }
      })
      .filter(Boolean),
  ), [audioVideo, remoteVideoTiles]);

  useEffect(() => {
    if (!rosterEntries.length) {
      return;
    }

    setSeenExternalIds((current) => {
      const next = new Set(current);
      rosterEntries.forEach((entry) => {
        if (entry?.externalUserId) {
          next.add(entry.externalUserId);
        }
      });
      return next;
    });
  }, [rosterEntries]);

  const normalizedUsers = useMemo(() => (users || []).map((user) => {
    const attendeeIds = getCandidateAttendeeIds(user);
    const expectedExternalId = getChimeExternalUserId(user);
    const matchingRosterEntry = rosterEntries.find((entry) => userMatchesRosterEntry(user, entry)) || null;
    const joined = Boolean(user?.self) || Boolean(matchingRosterEntry);
    const hasEverJoined = user?.self || (expectedExternalId ? seenExternalIds.has(expectedExternalId) : false);
    const joinState = joined
      ? 'joined'
      : (hasEverJoined ? 'left' : 'notJoined');
    const muted = user?.self
      ? Boolean(localMuted)
      : Boolean(matchingRosterEntry?.attendeeId && volumeIndicators[matchingRosterEntry.attendeeId]?.muted === true)
        || attendeeIds.some((id) => volumeIndicators[id]?.muted === true)
        || (expectedExternalId ? volumeIndicators[expectedExternalId]?.muted === true : false);
    const cameraOn = user?.self
      ? Boolean(localCameraOn)
      : attendeeIds.some((id) => activeVideoAttendeeIds.has(id))
        || (expectedExternalId ? activeVideoAttendeeIds.has(expectedExternalId) : false);

    return {
      ...user,
      attendeeIds,
      attendeeId: matchingRosterEntry?.attendeeId || user.attendeeId || attendeeIds[0] || '',
      joined,
      joinState,
      muted,
      cameraOn,
      rosterEntry: matchingRosterEntry,
      externalUserId: user.externalUserId || expectedExternalId,
    };
  }), [activeVideoAttendeeIds, localCameraOn, localMuted, rosterEntries, seenExternalIds, users, volumeIndicators]);

  return normalizedUsers;
};

export default useParticipantStatuses;